import {
  GetSignedUploadImageByKeyDocument,
  GetSignedUploadImageByKeyQuery,
  GetSignedUploadImageUrlDocument,
  GetSignedUploadImageUrlQuery,
} from '@/graphql/types/generated';
import { useCallback, useState } from 'react';
import { useClient } from 'urql';

type UploadResult = { key: string; url: string };

type UseImageUploadReturn = {
  upload: (file: File) => Promise<UploadResult | null>;
  uploading: boolean;
  error: string | null;
};

const useImageUpload = (): UseImageUploadReturn => {
  const client = useClient();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(
    async (file: File): Promise<UploadResult | null> => {
      const extension = file.name.split('.').pop();
      if (!extension) {
        setError('File has no extension');
        return null;
      }

      setUploading(true);
      setError(null);

      try {
        // Phase 1: Get signed upload URL
        const signedUrlResult = await client
          .query<GetSignedUploadImageUrlQuery>(
            GetSignedUploadImageUrlDocument,
            { extension },
            { requestPolicy: 'network-only' },
          )
          .toPromise();

        const signedUrl = signedUrlResult.data?.signedUploadImageUrl?.url;
        const key = signedUrlResult.data?.signedUploadImageUrl?.key;

        if (!signedUrl || !key) {
          setError('Failed to get upload URL');
          return null;
        }

        // Phase 2: Upload file to S3
        const uploadResponse = await fetch(signedUrl, {
          method: 'PUT',
          body: file,
        });
        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.status}`);
        }

        // Phase 3: Get permanent URL
        const permanentUrlResult = await client
          .query<GetSignedUploadImageByKeyQuery>(
            GetSignedUploadImageByKeyDocument,
            { key },
            { requestPolicy: 'network-only' },
          )
          .toPromise();

        const permanentUrl =
          permanentUrlResult.data?.signedUploadImageByKey?.url ?? null;

        if (!permanentUrl) {
          setError('Failed to get permanent URL');
          return null;
        }

        return { key, url: permanentUrl };
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Upload failed');
        return null;
      } finally {
        setUploading(false);
      }
    },
    [client],
  );

  return { upload, uploading, error };
};

export default useImageUpload;
